import { useState } from 'react';
import type { ReactNode } from 'react';
import { Drawer } from 'antd';
import { RedoOutlined } from '@ant-design/icons';

import { Badge, Button, Separator, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { BADGE_BAN_GHI, BADGE_GUI, BADGE_MUC, ICON_KENH } from '../constants';
import { useChiTietBanGhi, useGuiLai } from '../hooks/useCanhBao';
import { useDichCanhBao } from '../hooks/useDichCanhBao';
import { guiLaiDuoc } from '../nghiep-vu/canh-bao';
import type { NotifyDelivery, PhamViCanhBao } from '../types';
import { VungLoi } from './VungLoi';

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-content-muted">{nhan}</dt>
      <dd className="mt-0.5 break-words text-sm text-content-primary">{children ?? KHONG_CO}</dd>
    </div>
  );
}

/** Ngăn chi tiết một lần cảnh báo: nội dung, gửi qua từng kênh, dòng thời gian các lần thử, gửi lại. */
export function NganBanGhi({
  pv,
  id,
  duocSua,
  onClose,
}: {
  pv: PhamViCanhBao;
  id: string | null;
  duocSua: boolean;
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const dich = useDichCanhBao();
  const dichLoi = useLoi();
  const q = useChiTietBanGhi(pv, id);
  const r = q.data;

  return (
    <Drawer
      open={id !== null}
      onClose={onClose}
      title={t('canhBaoNhatKy.chiTiet')}
      placement="right"
      width={640}
      destroyOnHidden
    >
      {q.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : q.error || !r ? (
        <VungLoi cau={t('canhBaoNhatKy.loiChiTiet', { loi: dichLoi(q.error) })} onThuLai={() => void q.refetch()} />
      ) : (
        <div className="space-y-5">
          <section className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={BADGE_MUC[r.severity].variant}>{t(BADGE_MUC[r.severity].khoa)}</Badge>
              <Badge variant={BADGE_BAN_GHI[r.status].variant}>{t(BADGE_BAN_GHI[r.status].khoa)}</Badge>
            </div>
            <h3 className="break-words text-base font-semibold text-content-primary">{r.title}</h3>
            <dl className="grid gap-3 sm:grid-cols-2">
              <Dong nhan={t('canhBaoNhatKy.cotTacVu')}>{r.task_title ?? t('canhBaoNhatKy.tacVuDaXoa')}</Dong>
              <Dong nhan={t('canhBaoNhatKy.cotLoai')}>{dich.tenLoai(r.type_code)}</Dong>
              <Dong nhan={t('canhBaoNhatKy.xayRaLuc')}>{ngayGioVN(r.occurred_at, locale)}</Dong>
              {r.status === 'SUPPRESSED' && (
                <Dong nhan={t('canhBaoNhatKy.lyDo')}>{dich.lyDo(r.suppressed_reason)}</Dong>
              )}
            </dl>
            <div>
              <p className="text-xs text-content-muted">{t('canhBaoNhatKy.noiDung')}</p>
              <p className="mt-1 whitespace-pre-line break-words rounded bg-subtle px-3 py-2 text-sm text-content-secondary">
                {r.body}
              </p>
            </div>
          </section>

          <Separator />

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-content-primary">{t('canhBaoNhatKy.cacLanGui')}</h3>
            {r.deliveries.length === 0 ? (
              <p className="text-sm text-content-muted">{t('canhBaoNhatKy.khongGui')}</p>
            ) : (
              <ul className="space-y-3">
                {r.deliveries.map((d) => (
                  <LanGui key={d.id} pv={pv} d={d} duocSua={duocSua} />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

function LanGui({ pv, d, duocSua }: { pv: PhamViCanhBao; d: NotifyDelivery; duocSua: boolean }) {
  const { t, locale } = useT();
  const dich = useDichCanhBao();
  const guiLai = useGuiLai(pv);
  const [khoaLap] = useState(() => crypto.randomUUID());
  const Icon = ICON_KENH[d.channel_type];

  return (
    <li className="space-y-2 rounded-lg border border-default px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="text-content-muted" />
        <span className="min-w-0 truncate text-sm font-medium text-content-primary">
          {d.channel_name ?? dich.tenKenh(d.channel_type)}
        </span>
        <Badge variant={BADGE_GUI[d.status].variant}>{t(BADGE_GUI[d.status].khoa)}</Badge>
        <span className="text-2xs text-content-muted">
          {t('canhBaoNhatKy.soLanThu', { so: soVN(d.attempts, locale) })}
        </span>
        {duocSua && guiLaiDuoc(d) && d.channel_id && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            icon={<RedoOutlined />}
            loading={guiLai.isPending}
            onClick={() => guiLai.mutate({ deliveryId: d.id, khoaLap })}
          >
            {guiLai.isPending ? t('canhBaoNhatKy.dangGuiLai') : t('canhBaoNhatKy.guiLai')}
          </Button>
        )}
      </div>
      {d.replay_of && <p className="text-2xs text-content-muted">{t('canhBaoNhatKy.laGuiLai')}</p>}
      {(d.error_code || d.safe_error_message) && (
        <p className="break-words text-xs text-danger-700">
          {dich.loiGui(d.error_code)}
          {d.safe_error_message ? ` — ${d.safe_error_message}` : ''}
        </p>
      )}
      {d.status === 'RETRY_SCHEDULED' && d.next_attempt_at && (
        <p className="text-xs text-content-muted">
          {t('canhBaoNhatKy.thuLaiLuc', {
            luc: ngayGioVN(d.next_attempt_at, locale),
          })}
        </p>
      )}
      {(d.attempt_log ?? []).length > 0 && (
        <ol className="space-y-1 border-l border-default pl-3">
          {(d.attempt_log ?? []).map((a) => (
            <li key={a.attempt_no} className="text-xs text-content-secondary">
              <span className="font-medium">{t('canhBaoNhatKy.thuLan', { so: a.attempt_no })}</span>
              {' · '}
              <span className="tabular-nums">{ngayGioVN(a.started_at, locale)}</span>
              {' · '}
              {t(`canhBaoMa.ketQua.${a.outcome}` as KhoaDich)}
              {a.latency_ms != null && ` · ${t('canhBaoNhatKy.doTre', { so: soVN(a.latency_ms, locale) })}`}
              {a.http_status != null && ` · ${t('canhBaoNhatKy.http', { ma: a.http_status })}`}
              {a.error_code && ` · ${dich.loiGui(a.error_code)}`}
            </li>
          ))}
        </ol>
      )}
    </li>
  );
}
